import { useEffect, useState, FormEvent } from 'react';
import '../App.scss';
import { Button } from '@mui/base/Button';
import { TextField, FormControlLabel, Switch } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { useNavigate } from 'react-router-dom';
import { ResponseClient, AdminClient, RegisterUserModel, CompanySummary } from '../types';
import { createClient } from "../api/clients";

function Registration() {
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [companies, setCompanies] = useState<CompanySummary[]>([]);
  const [email, setEmail] = useState('');
  const [isEnabled, setIsEnabled] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const navigate = useNavigate();
  const responseClient = createClient(ResponseClient);
  const adminClient = createClient(AdminClient);

  useEffect(() => {
    const init = async () => {
      try {
        await responseClient.wakeUp();
      } catch (error) {
        console.error('Error during wake up call:', error);
      }

      try {
        const result = await adminClient.getCompanies();
        setCompanies(result ?? []);
      } catch (error) {
        console.error('Error fetching companies:', error);
        setErrorMessage('Could not load companies. Please refresh and try again.');
      }
    };

    void init();
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMessage(null);

    if (companyId == null) {
      setErrorMessage('Please select a company.');
      return;
    }

    const user: RegisterUserModel = {
      name,
      role,
      companyId: companyId!, 
      email,
      isEnabled
    };

    try {
      const result = await adminClient.registerUser(user);

      if (result.success === false) {
        setErrorMessage(result.error ?? null);
        return;
      }

      navigate(`../admin`, {
        replace: true,
        state: {
          message:
            'User invited successfully. They will receive an email with instructions to set their password.'
        }
      });
    } catch (error) {
      console.error('Error:', error);
      setErrorMessage('Something went wrong. Please try again.');
    }
  };

  const selectedCompany =
    companyId ? companies.find((c) => c.id === companyId) ?? null : null;

  return (
    <div className="main pb-4 d-flex flex-column align-items-center">
      <Button
        className="styled-button mb-4"
        onClick={() => navigate('/admin')}
      >
        ← Back
      </Button>

      <h4 className="mb-6">Create a lead user...</h4>

      <form onSubmit={handleSubmit} className="d-flex flex-column align-items-center">
        <TextField
          id="name"
          label="Name"
          variant="outlined"
          className="mb-4"
          value={name}
          style={{ minWidth: '300px' }}
          onChange={(e) => setName(e.target.value)}
          required
        />

        <TextField
          id="role"
          label="Role"
          variant="outlined"
          className="mb-4"
          style={{ minWidth: '300px' }}
          value={role}
          onChange={(e) => setRole(e.target.value)}
          required
        />

        <Autocomplete
          className="mb-4"
          options={companies}
          getOptionLabel={(option: CompanySummary) => option.name ?? ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedCompany}
          onChange={(_, newValue) => {
            setCompanyId(newValue?.id ?? null);
          }}
          style={{ minWidth: '300px' }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Company"
              required
            />
          )}
        />

        <TextField
          id="email"
          label="Email"
          variant="outlined"
          className="mb-4"
          style={{ minWidth: '300px' }}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />

        <FormControlLabel
          control={
            <Switch
              checked={isEnabled}
              onChange={(e) => setIsEnabled(e.target.checked)}
              color="primary"
            />
          }
          label="Enabled"
          className="mb-4"
        />

        {errorMessage && (
          <div className="form-error mb-3">
            {errorMessage}
          </div>
        )}

        <Button type="submit" className="styled-button mb-4" id="btn-register">
          Continue
        </Button>
        <p>* denotes mandatory fields</p>
      </form>
    </div>
  );
}

export default Registration;
